<template>
  <div 
    class="modal fade" 
    :id="id" 
    role="dialog" 
    tabindex="-1" 
    aria-hidden="true" 
    style="display: auto"
  >
    <div class="modal-dialog" :class="sizeClass" role="document">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header" v-if="!hideHeader">
          <h5 class="modal-title">
            <slot name="title">Título del Modal</slot>
          </h5>
          <ButtonProvider 
            ref="closeButton"
            class="close" 
            variant="link" 
            icon="fa-times" 
            data-dismiss="modal" 
            aria-label="Close"
          />
        </div>
        
        <!-- Slot para inyectar cabeceras personalizadas de borde a borde (como tabs) fuera del modal-body padding -->
        <slot name="custom-header" :hide="hide"></slot>

        <!-- El envoltorio es dinámico: form si es un formulario, div si es informativo -->
        <component :is="isForm ? 'form' : 'div'" @submit.prevent="handleSubmit">
          <div class="modal-body text-left">
            <slot name="body" :hide="hide"></slot>
          </div>
          <div class="modal-footer">
            <slot name="footer" :hide="hide">
              <!-- Botón Guardar solo aparece si es un formulario -->
              <ButtonProvider v-if="isForm" label="Guardar" type="submit" variant="success" />
              <ButtonProvider label="Cerrar" variant="danger" data-dismiss="modal" />
            </slot>
          </div>
        </component>
      </div>
    </div>
  </div>
</template>
<script>

/**
 * /--------------------------/
 * COMPONENTE: ModalProvider 
 * /--------------------------/
 * 
 * Este componente actúa como un contenedor (wrapper) para modales de Bootstrap,
 * permitiendo reutilizar la estructura y lógica de formularios o modales informativos.
 * 
 * --- INSTRUCCIONES DE USO POR SLOTS ---
 * 
 * @slot title
 *       Uso: `<template #title> Mi Título </template>`
 *       Contenido: Define el texto o HTML que aparecerá en la cabecera nativa del modal.
 * 
 * @slot custom-header
 *       Uso: `<template #custom-header> <ul class="nav">...</ul> </template>`
 *       Contenido: Permite inyectar contenido entre la cabecera nativa y el cuerpo (body) 
 *                  del modal. Es ideal para elementos como pestañas (tabs) que necesitan
 *                  ocupar el 100% del ancho (edge-to-edge) sin verse afectados por el 
 *                  padding interno de la clase `.modal-body`.
 * 
 * @slot body
 *       Uso: `<template #body="{ hide }"> ... </template>`
 *       Parámetros: {Function} hide - Permite cerrar el modal desde el interior del template.
 *       Contenido: Espacio principal para el formulario o información. 
 *       Nota: Si isForm=true, el contenido se envuelve en un <form>.
 * 
 * @slot footer
 *       Uso: `<template #footer="{ hide }"> ... </template>`
 *       Parámetros: {Function} hide - Permite cerrar el modal desde el interior del template.
 *       Contenido: Área para botones de acción. 
 *       Nota: Por defecto muestra "Guardar" (solo si isForm=true) y "Cerrar".
 * 
 * --- COMPORTAMIENTO INTELIGENTE (@submit) ---
 * Si isForm=true y el método asociado al evento @submit devuelve una Promesa (ej. DynamicService),
 * el modal esperará a que se resuelva con éxito para cerrarse automáticamente.
 * Si la promesa falla (catch), el modal permanece abierto para corregir errores.
 * 
 * --- PROPS ---
 * @prop {String} id 
 * (Requerido) - ID único para disparar el modal (data-target="#id").
 * 
 * @prop {String} size
 *  Tamaño del modal: 'sm', 'md', 'lg', 'xl'.
 * 
 * @prop {Boolean} hideHeader
 * Si es true, oculta por completo la cabecera nativa (.modal-header) y el botón de cerrar (X).
 * Ideal cuando se usa custom-header de borde a borde y no se requiere el título por defecto.
 * 
 * @prop {Boolean} isForm
 * Si es true, habilita el comportamiento de formulario (Default: false) 
 * vease de ejemplo: wms/recepcion/modals/RecepcionFacturaModalComponent.vue.
 */

import { ButtonProvider } from '../../atoms';

export default {
  name: 'ModalProvider',
  components: {
    ButtonProvider
  },
  props: {
    id: {
      type: String,
      required: true
    },
    size: {
      type: String,
      default: 'md' // md, lg, sm, xl
    },
    isForm: {
      type: Boolean,
      default: false
    },
    hideHeader: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    sizeClass() {
      return this.size !== 'md' ? `modal-${this.size}` : '';
    }
  },
  methods: {
    hide() {
      if (this.$refs.closeButton) {
        const el = this.$refs.closeButton.$el || this.$refs.closeButton;
        if (typeof el.click === 'function') {
          el.click();
        }
      }
    },
    /**
     * Maneja el envío del formulario.
     * Si el listener de 'submit' devuelve una promesa, espera a que se resuelva antes de cerrar.
     */
    async handleSubmit() {
      const handler = this.$listeners.submit;
      if (handler && this.isForm) {
        try {
          // Ejecutamos el handler. Si es asíncrono (API), devolverá una Promesa.
          const result = typeof handler === 'function' ? handler() : null;
          
          if (result instanceof Promise) {
            await result;
            this.hide();
          } else {
            // Si no es promesa, cerramos inmediatamente (comportamiento por defecto)
            this.hide();
          }
        } catch (error) {
          // Si la promesa falla (error de API), NO cerramos el modal
          console.error('Error en el envío del modal:', error);
        }
      } else if (!this.isForm) {
        // Si no es formulario, el submit (si ocurriera) cierra el modal
        this.hide();
      }
    }
  }
}
</script>